import axios from "axios";
import { useEffect } from "react";

// URL de base de l'API
const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8080/api/v1/";

// Création de l'instance Axios
const apiInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Hook personnalisé pour configurer les interceptors Axios
const useAxiosInterceptors = () => {
  useEffect(() => {
    const source = axios.CancelToken.source(); // Gestion d'annulation des requêtes

    // Intercepteur de requêtes
    const requestInterceptor = apiInstance.interceptors.request.use(
      (config) => {
        const token = JSON.parse(localStorage.getItem("tokens"))?.accessToken;
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Intercepteur de réponses
    const responseInterceptor = apiInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Vérifie si une réponse 401 ou 403 nécessite un rafraîchissement du token
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 403) &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true; // Marque la requête comme déjà réessayée

          try {
            // Rafraîchir le token
            const { data: refreshedToken } = await axios.post(
              `${baseURL}auth/refresh`,
              { refreshToken: JSON.parse(localStorage.getItem("tokens"))?.refreshToken }
            );

            // Mettre à jour le localStorage et les en-têtes
            localStorage.setItem("tokens", JSON.stringify(refreshedToken));
            originalRequest.headers["Authorization"] = `Bearer ${refreshedToken.accessToken}`;

            // Relancer la requête originale
            return apiInstance(originalRequest);
          } catch (refreshError) {
            // En cas d'échec du rafraîchissement, nettoyer le localStorage
            localStorage.removeItem("tokens");
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    // Nettoyage des interceptors et annulation des requêtes
    return () => {
      apiInstance.interceptors.request.eject(requestInterceptor);
      apiInstance.interceptors.response.eject(responseInterceptor);
      source.cancel("Axios request canceled.");
    };
  }, []);

  return apiInstance;
};

export default useAxiosInterceptors;
